import React, { useEffect, useState, useCallback } from 'react';

const STATUSES = ['', 'DRAFT', 'SUBMITTED', 'APPROVED', 'POSTED', 'PAID', 'REJECTED', 'CANCELLED'];

function PmsExpenseList({ token, selectedProperty }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState({
    from: monthStart,
    to: today,
    status: '',
    category_id: '',
    department_id: '',
    vendor_id: ''
  });
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  const loadMasters = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [c, d, v] = await Promise.all([
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() }),
        fetch('/api/v1/expense/vendors', { headers: headers() })
      ]);
      const [cd, dd, vd] = await Promise.all([c.json(), d.json(), v.json()]);
      setCategories(cd.rows || cd || []);
      setDepartments(dd.rows || dd || []);
      setVendors(vd.rows || vd || []);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) { setRows([]); return; }
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, val]) => { if (val) qs.append(k, val); });
      qs.append('limit', '500');
      const res = await fetch(`/api/v1/expense/?${qs.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load'); return; }
      setRows(data.rows || []);
      setError('');
      setPage(0);
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id, filters]);

  useEffect(() => { loadMasters(); }, [loadMasters]);
  useEffect(() => { loadData(); }, [loadData]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const loadDetail = async (id) => {
    try {
      const res = await fetch(`/api/v1/expense/${id}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load expense');
      setSelected(data);
    } catch { setError('Network error'); }
  };

  const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const statusColor = (s) => ({
    DRAFT: '#9ca3af', SUBMITTED: '#3b82f6', APPROVED: '#10b981',
    POSTED: '#0ea5e9', PAID: '#16a34a', REJECTED: '#ef4444', CANCELLED: '#ef4444'
  })[s] || '#6b7280';

  const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const visible = rows.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));

  const inputStyle = { padding: '0.45rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Expense List</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div className="error-banner" style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.8rem', marginBottom: '0.8rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem' }}>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}>
            <span>From</span>
            <input type="date" value={filters.from} onChange={e => setF('from', e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}>
            <span>To</span>
            <input type="date" value={filters.to} onChange={e => setF('to', e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}>
            <span>Status</span>
            <select value={filters.status} onChange={e => setF('status', e.target.value)} style={inputStyle}>
              {STATUSES.map(s => <option key={s} value={s}>{s || 'All'}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}>
            <span>Category</span>
            <select value={filters.category_id} onChange={e => setF('category_id', e.target.value)} style={inputStyle}>
              <option value="">All</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}>
            <span>Department</span>
            <select value={filters.department_id} onChange={e => setF('department_id', e.target.value)} style={inputStyle}>
              <option value="">All</option>
              {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.dept_name}</option>)}
            </select>
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.78rem' }}>
            <span>Vendor</span>
            <select value={filters.vendor_id} onChange={e => setF('vendor_id', e.target.value)} style={inputStyle}>
              <option value="">All</option>
              {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}
            </select>
          </label>
        </div>
        <div style={{ marginTop: '0.6rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <button onClick={loadData} disabled={loading} style={{ padding: '0.45rem 1rem', border: '1px solid #2563eb', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
            {loading ? 'Loading…' : 'Apply Filters'}
          </button>
          <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '0.85rem' }}>
            {rows.length} record{rows.length === 1 ? '' : 's'} — Total {fmtMoney(total)}
          </span>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.55rem' }}>Expense No</th>
                <th style={{ padding: '0.55rem' }}>Date</th>
                <th style={{ padding: '0.55rem' }}>Category</th>
                <th style={{ padding: '0.55rem' }}>Department</th>
                <th style={{ padding: '0.55rem' }}>Payee</th>
                <th style={{ padding: '0.55rem' }}>Mode</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Subtotal</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Tax</th>
                <th style={{ padding: '0.55rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.55rem' }}>Status</th>
                <th style={{ padding: '0.55rem' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={11} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No matching expenses.</td></tr>
              ) : visible.map(r => {
                const tax = Number(r.cgst_amount || 0) + Number(r.sgst_amount || 0) + Number(r.igst_amount || 0);
                const catName = categories.find(c => c.category_id === r.category_id)?.category_name || '—';
                const deptName = departments.find(d => d.department_id === r.department_id)?.dept_name || '—';
                return (
                  <tr key={r.expense_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{r.expense_no || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{r.expense_date}</td>
                    <td style={{ padding: '0.5rem' }}>{catName}</td>
                    <td style={{ padding: '0.5rem' }}>{deptName}</td>
                    <td style={{ padding: '0.5rem' }}>{r.payee_name || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{r.payment_mode}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(r.subtotal)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(tax)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.total_amount)}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: statusColor(r.status) + '22', color: statusColor(r.status), fontSize: '0.72rem', fontWeight: 600 }}>{r.status}</span>
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      <button onClick={() => loadDetail(r.expense_id)} style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
          <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Page {page + 1} of {totalPages}</span>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>Prev</button>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>Next</button>
          </div>
        </div>
      </div>

      {selected && (
        <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '1.2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
              <h3 style={{ margin: 0 }}>Expense {selected.header?.expense_no}</h3>
              <button onClick={() => setSelected(null)} style={{ border: 'none', background: 'transparent', fontSize: '1.4rem', cursor: 'pointer' }}>×</button>
            </div>
            {selected.header && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem', fontSize: '0.85rem' }}>
                <div><strong>Date:</strong> {selected.header.expense_date}</div>
                <div><strong>Status:</strong> {selected.header.status}</div>
                <div><strong>Payee:</strong> {selected.header.payee_name || '—'}</div>
                <div><strong>Mode:</strong> {selected.header.payment_mode}</div>
                <div><strong>Invoice:</strong> {selected.header.invoice_no || '—'}</div>
                <div><strong>Subtotal:</strong> {fmtMoney(selected.header.subtotal)}</div>
                <div><strong>Tax:</strong> {fmtMoney(Number(selected.header.cgst_amount || 0) + Number(selected.header.sgst_amount || 0) + Number(selected.header.igst_amount || 0))}</div>
                <div><strong>TDS:</strong> {fmtMoney(selected.header.tds_amount)}</div>
                <div style={{ gridColumn: '1 / -1' }}><strong>Total:</strong> <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{fmtMoney(selected.header.total_amount)}</span></div>
                <div style={{ gridColumn: '1 / -1' }}><strong>Description:</strong> {selected.header.description || '—'}</div>
              </div>
            )}
            {Array.isArray(selected.lines) && selected.lines.length > 0 && (
              <>
                <h4 style={{ marginTop: '1rem' }}>Lines</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead><tr style={{ background: '#f3f4f6' }}><th style={{ padding: '0.4rem', textAlign: 'left' }}>#</th><th style={{ padding: '0.4rem', textAlign: 'left' }}>Description</th><th style={{ padding: '0.4rem', textAlign: 'right' }}>Qty</th><th style={{ padding: '0.4rem', textAlign: 'right' }}>Rate</th><th style={{ padding: '0.4rem', textAlign: 'right' }}>Total</th></tr></thead>
                  <tbody>
                    {selected.lines.map(l => (
                      <tr key={l.line_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.4rem' }}>{l.line_no}</td>
                        <td style={{ padding: '0.4rem' }}>{l.item_description}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{l.quantity}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmtMoney(l.unit_rate)}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>{fmtMoney(l.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PmsExpenseList;
